import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Countdown, COLOR_STYLES } from '../types/database';
import { Confetti } from './Confetti';
import { supabase } from '../lib/supabase';

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isZero: boolean;
}

export function SharedCountdown() {
  const { shareId } = useParams<{ shareId: string }>();
  const [countdown, setCountdown] = useState<Countdown | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isZero: false
  });
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    const loadCountdown = async () => {
      if (!shareId) return;

      try {
        const { data, error } = await supabase
          .from('countdowns')
          .select('*')
          .eq('share_id', shareId)
          .maybeSingle();

        if (error) throw error;
        setCountdown(data);

        if (data) {
          updateMetaTags(data);
        }
      } catch (error) {
        console.error('Error loading shared countdown:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCountdown();
  }, [shareId]);

  const updateMetaTags = (countdown: Countdown) => {
    const dateStr = countdown.event_date.split('T')[0];
    const eventDate = new Date(dateStr + 'T00:00:00');
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diffTime = eventDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const formattedDate = eventDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    let description = '';
    if (diffDays > 0) {
      description = `${countdown.emoji} ${diffDays} ${diffDays === 1 ? 'day' : 'days'} until ${formattedDate}`;
    } else if (diffDays === 0) {
      description = `${countdown.emoji} Today is the day! ${formattedDate}`;
    } else {
      description = `${countdown.emoji} ${formattedDate}`;
    }

    const title = `${countdown.name} — Countdowns`;

    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'Countdowns' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ];

    metaTags.forEach(({ name, property, content }) => {
      const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement('meta');
        if (name) meta.name = name;
        if (property) meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }

      meta.content = content;
    });
  };

  useEffect(() => {
    if (!countdown) return;

    const calculateTimeRemaining = () => {
      const dateStr = countdown.event_date.split('T')[0];
      const eventDate = new Date(dateStr + 'T23:59:59');
      const now = new Date();
      const difference = eventDate.getTime() - now.getTime();

      if (difference <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0, isZero: true });
        if (!showConfetti && difference > -1000) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 5000);
        }
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds, isZero: false });
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [countdown, showConfetti]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-gray-600 text-xl">Loading...</div>
      </div>
    );
  }

  if (!countdown) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col items-center justify-center p-4">
        <div className="text-6xl mb-4">🤔</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Countdown Not Found</h1>
        <p className="text-gray-600 mb-8">This countdown may have been deleted or the link is invalid.</p>
        <Link
          to="/"
          className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200"
        >
          Create Your Own Countdowns
        </Link>
      </div>
    );
  }

  const colorStyle = COLOR_STYLES[countdown.color as keyof typeof COLOR_STYLES] || COLOR_STYLES.blue;

  return (
    <div className={`min-h-screen ${colorStyle.detail} flex flex-col items-center justify-center p-4 relative`}>
      {showConfetti && <Confetti />}

      <div className="text-center max-w-4xl w-full">
        <div className="text-5xl sm:text-6xl md:text-7xl lg:text-9xl mb-8 animate-bounce">{countdown.emoji}</div>

        <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold ${colorStyle.text} mb-8 px-4`}>
          {countdown.name}
        </h1>

        <div className={`text-base sm:text-lg md:text-xl lg:text-2xl ${colorStyle.text} opacity-80 mb-12 px-4`}>
          {(() => {
            const dateStr = countdown.event_date.split('T')[0];
            const date = new Date(dateStr + 'T00:00:00');
            return date.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
          })()}
        </div>

        {!timeRemaining.isZero ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 lg:gap-8 mb-12">
            <div className="bg-white/30 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8">
              <div className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold ${colorStyle.text} mb-2`}>
                {timeRemaining.days}
              </div>
              <div className={`text-xs sm:text-sm md:text-base lg:text-lg ${colorStyle.text} opacity-70`}>Days</div>
            </div>
            <div className="bg-white/30 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8">
              <div className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold ${colorStyle.text} mb-2`}>
                {timeRemaining.hours}
              </div>
              <div className={`text-xs sm:text-sm md:text-base lg:text-lg ${colorStyle.text} opacity-70`}>Hours</div>
            </div>
            <div className="bg-white/30 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8">
              <div className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold ${colorStyle.text} mb-2`}>
                {timeRemaining.minutes}
              </div>
              <div className={`text-xs sm:text-sm md:text-base lg:text-lg ${colorStyle.text} opacity-70`}>Minutes</div>
            </div>
            <div className="bg-white/30 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8">
              <div className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold ${colorStyle.text} mb-2`}>
                {timeRemaining.seconds}
              </div>
              <div className={`text-xs sm:text-sm md:text-base lg:text-lg ${colorStyle.text} opacity-70`}>Seconds</div>
            </div>
          </div>
        ) : (
          <div className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold ${colorStyle.text} mb-12 px-4`}>
            The event is here! 🎉
          </div>
        )}

        <Link
          to="/"
          className="inline-block px-8 py-4 bg-white text-gray-900 font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
        >
          Create Your Own Countdowns
        </Link>
      </div>
    </div>
  );
}